/**
 * EditableQuestionnaireForm — the in-progress editable view in the
 * Forms tab (PROD-845 PR-2). Replaces the "Awaiting response"
 * placeholder that shipped in PR-1.
 *
 * Renders the patient-facing questionnaire so a clinician / staff can
 * fill it on the patient's behalf during the visit. Five question
 * types — String / Boolean / Choice / OpenChoice / Group — with
 * recursive Group nesting and Plan A completeness check (recurses
 * properly into nested Groups; see `questionnaire-form-state.ts`).
 *
 * Save flow:
 *   1. User fills in answers; local form state updates per-keystroke
 *      via `updateItem` (immutable patch).
 *   2. Save button gated by `isQuestionnaireComplete` over the form
 *      state. On click with incomplete: verbatim error toast
 *      `"Please provide response to all the mandatory fields"` — same
 *      string Flutter ships at `appointment_form_widget.dart:140-141`.
 *   3. On Save with complete: project to wire payload via
 *      `formStateToWirePayload`, fire `useSaveQuestionnaireResponse`,
 *      which invalidates the PR-1 GET — the tab observer refetches
 *      and transitions to the submitted view automatically.
 *
 * Backend behaviour (verified 2026-05-20 in
 * `Scrips.PracticeManagement/.../QuestionnaireResponseService.cs`):
 *   - REQUIRES an existing `InProgress` `QuestionnaireResponses` row.
 *     Created by the appointment-booking flow when a form is attached.
 *   - 200 with body `true` on success. 400 with `QuestionnaireNotExist`
 *     if the InProgress row is missing — surfaced via the global
 *     QueryClient `onError` handler.
 *   - Backend does NOT validate completeness; this form's required-
 *     field rules are a pure client-side UX gate.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Checkbox } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { Label } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { TextArea as Textarea } from '@scripsteam/scrips-signal-ds';
import { useQuestionnaireDefinition } from '@/scheduling/services/use-questionnaire-definition';
import { useSaveQuestionnaireResponse } from '@/scheduling/services/use-save-questionnaire-response';
import {
  MAX_QUESTIONNAIRE_NESTING_DEPTH,
  definitionToFormState,
  formStateToWirePayload,
  getMaxNestingDepth,
  isQuestionnaireComplete,
  updateItem,
  type QuestionnaireFormItem,
} from '@/scheduling/lib/questionnaire-form-state';
import type {
  AppointmentResponse,
  QuestionnaireAnswerOption,
  QuestionnaireDefinitionItem,
  QuestionnaireValueCoding,
} from '@/types/scheduling-api';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.
import { BrandSpinner } from '@scripsteam/scrips-signal-ds';

interface EditableQuestionnaireFormProps {
  appointment: AppointmentResponse;
  /** From PR-1's GET response — drives subject/sourceId on save. */
  patientId: string;
}

export function EditableQuestionnaireForm({
  appointment,
  patientId,
}: EditableQuestionnaireFormProps) {
  const formId = appointment.questionnaireForm?.questionnaireId ?? null;
  const practiceId = appointment.practiceId ?? null;
  // Provider is the participant whose `provider.id` is set. Flutter
  // pulls from `selectedAppointment.selectedDoctor.id`; the React
  // appointment shape carries it via the participant array.
  const practitionerId =
    appointment.participant?.find((p) => p.provider)?.provider?.id ?? null;

  const { data: definition, isLoading, isError } = useQuestionnaireDefinition({
    practitionerId,
    practiceId,
    formId,
  });

  // Local form state mirrors the definition tree. Initialised once
  // definition lands; we don't re-initialise on definition refetch
  // (would clobber in-progress user input).
  const [items, setItems] = useState<QuestionnaireFormItem[] | null>(null);
  useEffect(() => {
    if (!definition || items) return;
    setItems(definitionToFormState(definition));
  }, [definition, items]);

  // Build a map of `questionId → answerOptions[]` so the per-item
  // renderer can grab options without walking the definition tree.
  const answerOptionsMap = useMemo(
    () => collectAnswerOptions(definition?.items ?? []),
    [definition],
  );

  const saveMutation = useSaveQuestionnaireResponse();

  const handleChange = (questionId: string, patch: Partial<QuestionnaireFormItem>) => {
    setItems((prev) => (prev ? updateItem(prev, questionId, patch) : prev));
  };

  const handleSave = () => {
    if (!items || !formId || !patientId || !practitionerId) return;
    // Defence-in-depth: the Save button is already gated by
    // `isQuestionnaireComplete` below, but `formStateToWirePayload`
    // runs the same check internally and refuses to build a payload
    // if anything required is unanswered. Single source of truth —
    // see PR #262 Samer review §2.
    const result = formStateToWirePayload(items, {
      questionnaireId: formId,
      subject: patientId,
      author: practitionerId,
      appointmentId: appointment.id,
      sourceId: null,
    });
    if (!result.ok) {
      // Verbatim Flutter string — `appointment_form_widget.dart:140-141`.
      toast.error('Please provide response to all the mandatory fields');
      return;
    }
    saveMutation.mutate({ appointmentId: appointment.id, payload: result.payload });
  };

  if (isLoading || !items) {
    return (
      <div data-slot="appt-tab-page" className="qf-skel">
        <Skeleton className="qf-sk1" />
        <Skeleton className="qf-sk2" />
        <Skeleton className="qf-sk2" />
        <Skeleton className="qf-sk2" />
      </div>
    );
  }

  // Definition fetch outcomes (Samer PR #262 review §4):
  //   - `isError`: network/server failure during the POST
  //   - `!definition`: query returned but data is null/undefined
  //   - `definition.items` empty AND items.length === 0: backend
  //     returned a questionnaire with no questions. We refuse to
  //     render an empty form the user could submit blank (recording
  //     `Completed` with no answers).
  if (isError || !definition) {
    return (
      <FormErrorState
        title="Couldn't load the form"
        message="The questionnaire failed to load. Please reopen the appointment to try again."
      />
    );
  }
  if (items.length === 0) {
    return (
      <FormErrorState
        title="Empty questionnaire"
        message="This form has no questions configured. Contact support before continuing."
      />
    );
  }

  // Hard max-nesting check (Samer PR #262 review §5): refuse to
  // render rather than silently truncating. Silent truncation would
  // let staff submit `Completed` for a form where deep questions
  // were never visible.
  const maxDepth = getMaxNestingDepth(items);
  if (maxDepth > MAX_QUESTIONNAIRE_NESTING_DEPTH) {
    return (
      <FormErrorState
        title="Form too deeply nested"
        message={`This questionnaire has Group nesting of depth ${maxDepth}, exceeding the supported maximum of ${MAX_QUESTIONNAIRE_NESTING_DEPTH}. The form cannot be filled in until the questionnaire is updated. Contact support.`}
      />
    );
  }

  // Save button gated by completeness for the visual affordance. The
  // payload builder ALSO checks completeness as a second line of
  // defence (`formStateToWirePayload` returns `{ok:false}` if
  // incomplete) — see PR #262 Samer review §2.
  const saveEnabled =
    !saveMutation.isPending && items.length > 0 && isQuestionnaireComplete(items);

  return (
    <div data-slot="appt-tab-page">
      <div className="qf-head">
        <h2 data-slot="appt-tab-title">
          {definition.title ?? appointment.questionnaireForm?.name ?? 'Questionnaire'}
        </h2>
        {definition.description ? (
          <p className="qf-desc">{definition.description}</p>
        ) : null}
      </div>

      <ol className="qf-list">
        {items.map((item) => (
          <li
            key={item.questionId}
            className="qf-card"
          >
            <EditableItemRow
              item={item}
              depth={0}
              answerOptionsMap={answerOptionsMap}
              onChange={handleChange}
              readOnly={saveMutation.isPending}
            />
          </li>
        ))}
      </ol>

      <div className="qf-foot">
        {saveMutation.isPending ? (
          <span className="qf-saving">
            <BrandSpinner size={16} label="Saving" />
            Saving…
          </span>
        ) : null}
        <Button
          type="button"
          onClick={handleSave}
          disabled={!saveEnabled}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

// ── Recursive item row ──────────────────────────────────────────────────
//
// Note: NO silent depth-cap truncation here. The parent component
// runs `getMaxNestingDepth` against the form-state tree BEFORE
// mounting this renderer and refuses to render the form at all if
// nesting exceeds `MAX_QUESTIONNAIRE_NESTING_DEPTH`. So this
// recursive renderer can assume depth is in bounds.

function EditableItemRow({
  item,
  depth,
  answerOptionsMap,
  onChange,
  readOnly,
}: {
  item: QuestionnaireFormItem;
  depth: number;
  answerOptionsMap: Map<string, QuestionnaireAnswerOption[]>;
  onChange: (questionId: string, patch: Partial<QuestionnaireFormItem>) => void;
  readOnly: boolean;
}) {
  const indent = depth > 0 ? 'qf-nest' : '';

  if (item.type === 'Group') {
    return (
      <div className={indent}>
        <p className="qf-group">
          {item.text || 'Group'}
        </p>
        <div className="qf-group-body">
          {item.items.map((child) => (
            <EditableItemRow
              key={child.questionId}
              item={child}
              depth={depth + 1}
              answerOptionsMap={answerOptionsMap}
              onChange={onChange}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={indent}>
      <Label className="qf-label">
        {item.text || 'Question'}
        {item.required ? <span className="qf-req">*</span> : null}
      </Label>
      {renderInput(item, answerOptionsMap, onChange, readOnly)}
    </div>
  );
}

function renderInput(
  item: QuestionnaireFormItem,
  answerOptionsMap: Map<string, QuestionnaireAnswerOption[]>,
  onChange: (questionId: string, patch: Partial<QuestionnaireFormItem>) => void,
  readOnly: boolean,
) {
  switch (item.type) {
    case 'String':
      return <StringInput item={item} onChange={onChange} readOnly={readOnly} />;
    case 'Boolean':
      return <BooleanInput item={item} onChange={onChange} readOnly={readOnly} />;
    case 'Choice':
      return (
        <ChoiceInput
          item={item}
          options={answerOptionsMap.get(item.questionId) ?? []}
          multi={false}
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    case 'OpenChoice':
      return (
        <ChoiceInput
          item={item}
          options={answerOptionsMap.get(item.questionId) ?? []}
          multi
          onChange={onChange}
          readOnly={readOnly}
        />
      );
    default:
      return (
        <p className="qf-italic">
          Unsupported question type: {item.type}
        </p>
      );
  }
}

// ── Per-type input renderers ────────────────────────────────────────────

function StringInput({
  item,
  onChange,
  readOnly,
}: {
  item: QuestionnaireFormItem;
  onChange: (questionId: string, patch: Partial<QuestionnaireFormItem>) => void;
  readOnly: boolean;
}) {
  // Use Textarea for any question with maxLength > 100 or unbounded;
  // single-line Input otherwise. Flutter renders both with a single
  // text field but the React idiom prefers Textarea for long answers.
  const useTextarea = true;
  const Cmp = useTextarea ? Textarea : TextField;
  // PROD-846: enforce backend `maxLength` at the HTML layer + render a
  // `${value.length}/${maxLength}` counter underneath. `maxLength` is
  // forwarded through QuestionnaireFormItem (see definitionToFormState).
  // `undefined` means no cap → no `maxLength` attr, no counter.
  const maxLength = item.maxLength;
  const showCounter = typeof maxLength === 'number' && maxLength > 0;
  const valueLength = (item.valueString ?? '').length;
  // Defence-in-depth: HTML `maxLength` blocks typing past the cap, but
  // a paste of (N > cap) chars could slip through in older browsers.
  // We also clamp on change — net effect is the value is never longer
  // than `maxLength`.
  const handleChange = (next: string) => {
    const clamped =
      typeof maxLength === 'number' && maxLength > 0 && next.length > maxLength
        ? next.slice(0, maxLength)
        : next;
    onChange(item.questionId, { valueString: clamped });
  };
  return (
    <div>
      <Cmp
        value={item.valueString}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Enter your answer"
        disabled={readOnly}
        rows={useTextarea ? 2 : undefined}
        maxLength={maxLength}
      />
      {showCounter ? (
        <p
          className="qf-counter"
          aria-live="polite"
        >
          {valueLength}/{maxLength}
        </p>
      ) : null}
    </div>
  );
}

function BooleanInput({
  item,
  onChange,
  readOnly,
}: {
  item: QuestionnaireFormItem;
  onChange: (questionId: string, patch: Partial<QuestionnaireFormItem>) => void;
  readOnly: boolean;
}) {
  // Two-button group so the "not yet picked" state is visible (vs a
  // Switch which conflates No with unset). Matches Flutter's
  // BooleanAnswerWidget which renders Yes/No buttons.
  const pickYes = () => onChange(item.questionId, { valueBoolean: true });
  const pickNo = () => onChange(item.questionId, { valueBoolean: false });
  return (
    <div className="qf-picks">
      <PickButton
        active={item.valueBoolean === true}
        onClick={pickYes}
        disabled={readOnly}
      >
        Yes
      </PickButton>
      <PickButton
        active={item.valueBoolean === false}
        onClick={pickNo}
        disabled={readOnly}
      >
        No
      </PickButton>
    </div>
  );
}

function ChoiceInput({
  item,
  options,
  multi,
  onChange,
  readOnly,
}: {
  item: QuestionnaireFormItem;
  options: QuestionnaireAnswerOption[];
  multi: boolean;
  onChange: (questionId: string, patch: Partial<QuestionnaireFormItem>) => void;
  readOnly: boolean;
}) {
  if (options.length === 0) {
    return (
      <p className="qf-italic">
        No answer options defined.
      </p>
    );
  }

  const isSelected = (vc: QuestionnaireValueCoding) =>
    item.valueCoding.some((v) => v.code === vc.code && v.system === vc.system);

  const toggle = (vc: QuestionnaireValueCoding) => {
    if (multi) {
      const already = isSelected(vc);
      const next = already
        ? item.valueCoding.filter(
            (v) => !(v.code === vc.code && v.system === vc.system),
          )
        : [...item.valueCoding, vc];
      onChange(item.questionId, { valueCoding: next });
    } else {
      // Single-select: replace with the picked one (or clear if re-clicked).
      const already = isSelected(vc);
      onChange(item.questionId, { valueCoding: already ? [] : [vc] });
    }
  };

  return (
    <div className="qf-options">
      {options.map((opt, i) => {
        const vc = opt.valueCoding;
        if (!vc?.code) return null;
        const checked = isSelected(vc);
        // DS `Checkbox` renders its OWN internal `<label htmlFor={id}>`
        // wrapping the input (+ its `label` prop's text). The previous local
        // checkbox did NOT own a label element, so it was safe to wrap it in
        // this component's own outer `<label>` + a sibling `<span>` for the
        // option text, with an invisible `aria-label` mirroring that text on
        // the input itself. Nesting this component's `<label>` around the
        // DS control's own `<label>` would be invalid HTML (nested labels —
        // unreliable click delegation / name resolution), so that shape is
        // REFUSED, not carried over. Fix: use the DS's own `label` prop for
        // the visible option text (it needed no invisible `aria-label`
        // anyway — the text was already visible) and move the row styling
        // onto the DS `className`, which the component merges into its own
        // wrapper.
        return (
          <Checkbox
            key={`${vc.code}-${i}`}
            checked={checked}
            onCheckedChange={() => toggle(vc)}
            disabled={readOnly}
            shape={multi ? 'square' : 'circle'}
            label={vc.display ?? vc.code}
            className="qf-option"
            data-readonly={readOnly || undefined}
          />
        );
      })}
    </div>
  );
}

function PickButton({
  active,
  children,
  onClick,
  disabled,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="qf-pick"
      data-active={active || undefined}
    >
      {children}
    </button>
  );
}

// ── Definition walk helpers ─────────────────────────────────────────────

/**
 * Build `questionId → answerOptions[]` map by walking the definition
 * tree once. Recurses into Groups so nested Choice/OpenChoice items
 * have their options available without re-walking.
 */
function collectAnswerOptions(
  items: QuestionnaireDefinitionItem[],
): Map<string, QuestionnaireAnswerOption[]> {
  const map = new Map<string, QuestionnaireAnswerOption[]>();
  const walk = (list: QuestionnaireDefinitionItem[]) => {
    for (const item of list) {
      if (item.id && (item.answerOptions?.length ?? 0) > 0) {
        map.set(item.id, item.answerOptions ?? []);
      }
      if (item.items?.length) walk(item.items);
    }
  };
  walk(items);
  return map;
}

// ── Error state ────────────────────────────────────────────────────────

/**
 * Prominent error banner used for the three terminal cases in
 * `EditableQuestionnaireForm` — fetch failure, empty questionnaire,
 * and nesting exceeded. Each case prevents the user from submitting
 * a payload that would be silently incomplete.
 */
function FormErrorState({
  title,
  message,
}: {
  title: string;
  message: string;
}) {
  return (
    <div data-slot="appt-tab-page">
      <div className="qf-banner">
        <AlertTriangle className="size-5" />
        <div className="min-w-0">
          <p className="qf-banner-title">{title}</p>
          <p className="qf-banner-msg">{message}</p>
        </div>
      </div>
    </div>
  );
}
